import { useQuery } from '@tanstack/react-query'
import { apiFetch, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import { isDemoSession } from '@/auth/oidc'
import {
  mapAggregateAccountsSearch,
  type AggregateAccountApiRow,
  type AggregateAccountsSearchResult,
} from '@/api/map/aggregateAccounts'
import { filterDemoAggregateAccounts } from '@/features/aggregate-accounts/demoAccounts'
import { keys } from '@/api/hooks/shared/keys'

type SearchEnvelope = {
  status?: number
  message?: string
  data?: { rows?: AggregateAccountApiRow[] }
  rows?: AggregateAccountApiRow[]
}

async function searchAggregateAccountsLive(
  username: string,
  parentId?: string,
): Promise<AggregateAccountsSearchResult> {
  const body: { username?: string; parentId?: string } = {}
  if (username.trim()) body.username = username.trim()
  if (parentId) body.parentId = parentId
  const res = await apiFetch<SearchEnvelope>('/v1/aggregate-members/search', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  const rows = res?.data?.rows ?? res?.rows ?? []
  return mapAggregateAccountsSearch(rows)
}

async function searchAggregateAccounts(
  username: string,
  parentId?: string,
): Promise<AggregateAccountsSearchResult> {
  if (isDemoSession() || !hasLiveApi()) {
    return mapAggregateAccountsSearch(filterDemoAggregateAccounts(username, parentId))
  }
  try {
    return await searchAggregateAccountsLive(username, parentId)
  } catch (err) {
    if (isUnreachableApiError(err)) {
      return mapAggregateAccountsSearch(filterDemoAggregateAccounts(username, parentId))
    }
    throw err
  }
}

export function useAggregateAccounts(username: string, parentId?: string) {
  return useQuery({
    queryKey: keys.aggregateAccounts(username, parentId),
    queryFn: () => searchAggregateAccounts(username, parentId),
    placeholderData: (previous) => previous,
  })
}
